import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, AlertTriangle, Eye, EyeOff } from 'lucide-react';

export default function AdminLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setErr(null);
    try {
      await login('admin', { email, password, remember });
      toast.success('Signed in — welcome back');
      nav('/admin/dashboard');
    } catch (e2) {
      setErr(e2?.message || 'Sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      <div className="absolute inset-0 gradient-mesh opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-teal-500/10 blur-3xl" />

      <div className="relative min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-xl bg-white/10 border border-white/20 grid place-items-center font-bold text-white">SE</div>
            <div>
              <div className="font-semibold text-white leading-tight tracking-tight">StayEasy</div>
              <div className="text-[10.5px] text-slate-400 leading-tight uppercase tracking-[0.09em] font-medium mt-0.5">Admin Console</div>
            </div>
          </Link>

          <div className="rounded-2xl bg-slate-900/80 backdrop-blur border border-slate-800 p-8 shadow-2xl">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/10 text-amber-300 text-[11px] font-semibold border border-amber-400/20 mb-4">
              <ShieldCheck className="h-3 w-3" strokeWidth={2.5} /> Restricted access
            </div>
            <h1 className="text-[24px] font-semibold text-white tracking-[-0.024em]">Platform admin sign-in</h1>
            <p className="text-slate-400 mt-1.5 text-[13.5px]">Only StayEasy team members with valid credentials can access this area.</p>

            {err && (
              <div className="mt-5 flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 text-[13px] text-rose-300">
                <AlertTriangle className="h-4 w-4 mt-0.5" /> <span>{err}</span>
              </div>
            )}

            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="aemail" className="text-slate-300">Work email</Label>
                <Input id="aemail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" placeholder="you@stayeasy.com" required autoComplete="email" />
              </div>
              <div>
                <Label htmlFor="apassword" className="text-slate-300">Password</Label>
                <div className="relative mt-1.5">
                  <Input id="apassword" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-11 bg-slate-800 border-slate-700 text-white" required autoComplete="current-password" />
                  <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} className="absolute inset-y-0 right-0 grid w-11 place-items-center text-slate-500 transition-colors hover:text-white">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-[13px] text-slate-400">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-teal-500" /> Keep me signed in
              </label>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-teal-600 hover:bg-teal-500 text-white">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
              </Button>
            </form>

            <p className="mt-6 text-xs text-slate-500 text-center">Are you a host? <Link to="/host/login" className="underline text-slate-300">Sign in here</Link></p>
          </div>

          <p className="mt-6 text-center text-xs text-slate-500">All administrative actions are logged and auditable.</p>
        </div>
      </div>
    </div>
  );
}
